import { apiClient } from "@/lib/api/client";
import type {
  IsoDateTimeString,
  TelemetryAggregatesResponseDto,
  TelemetryBatchQueryRequestDto,
  TelemetryBatchQueryResponseDto,
  TelemetryDto,
  TelemetryPageResponseDto,
} from "@/lib/api/types";

export type TelemetryTimeRange = {
  start?: Date | IsoDateTimeString;
  end?: Date | IsoDateTimeString;
};

export type TelemetrySortOrder = "asc" | "desc";

export type TelemetryBatchQueryParams = {
  workspaceScope?: "all" | null;
  dashboardId?: string | null;
  moduleKey?: string | null;
};

function toQueryIso(value: Date | IsoDateTimeString | undefined): string | undefined {
  if (value === undefined) {
    return undefined;
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  return value;
}

export type TelemetryAggregatesParams = {
  deviceId: string;
  start: Date | IsoDateTimeString;
  end: Date | IsoDateTimeString;
  bucketSeconds?: number;
  maxPoints?: number;
  metrics?: string;
  workspaceScope?: "all" | null;
  dashboardId?: string | null;
  moduleKey?: string | null;
};

export async function getTelemetryAggregates(
  params: TelemetryAggregatesParams,
): Promise<TelemetryAggregatesResponseDto> {
  const bucketSeconds = params.bucketSeconds ?? 300;
  const maxPoints = params.maxPoints ?? 8192;
  const query: Record<string, string | number> = {
    device_id: params.deviceId,
    start: toQueryIso(params.start) ?? "",
    end: toQueryIso(params.end) ?? "",
    bucket_seconds: bucketSeconds,
    max_points: maxPoints,
  };
  if (params.metrics) {
    query.metrics = params.metrics;
  }
  if (params.workspaceScope === "all") {
    query.workspace_scope = "all";
  }
  if (params.dashboardId) {
    query.dashboard_id = params.dashboardId;
  }
  if (params.moduleKey) {
    query.module_key = params.moduleKey;
  }
  const { data } = await apiClient.get<TelemetryAggregatesResponseDto>("/telemetry/aggregates", {
    params: query,
    __skipTenantHeader: params.workspaceScope === "all",
  });
  return data;
}

export async function getLiveTelemetryAggregates(
  params: TelemetryAggregatesParams,
): Promise<TelemetryAggregatesResponseDto> {
  const bucketSeconds = params.bucketSeconds ?? 60;
  const maxPoints = params.maxPoints ?? 8192;
  const query: Record<string, string | number> = {
    device_id: params.deviceId,
    start: toQueryIso(params.start) ?? "",
    end: toQueryIso(params.end) ?? "",
    bucket_seconds: bucketSeconds,
    max_points: maxPoints,
  };
  if (params.metrics) {
    query.metrics = params.metrics;
  }
  if (params.workspaceScope === "all") {
    query.workspace_scope = "all";
  }
  if (params.dashboardId) {
    query.dashboard_id = params.dashboardId;
  }
  const { data } = await apiClient.get<TelemetryAggregatesResponseDto>("/telemetry/live/aggregates", {
    params: query,
    __skipTenantHeader: params.workspaceScope === "all",
  });
  return data;
}

/** Raw rows — avoid for large windows; prefer getTelemetryAggregates. */
export async function postTelemetryBatchQuery(
  body: TelemetryBatchQueryRequestDto,
  params?: TelemetryBatchQueryParams,
): Promise<TelemetryBatchQueryResponseDto> {
  const { data } = await apiClient.post<TelemetryBatchQueryResponseDto>("/telemetry/batch-query", body, {
    __skipTenantHeader: params?.workspaceScope === "all",
  });
  return data;
}

export async function getTelemetry(
  deviceId: string,
  timeRange?: TelemetryTimeRange,
  workspaceScope?: "all" | null,
  dashboardId?: string | null,
  moduleKey?: string | null,
): Promise<TelemetryDto[]> {
  const params: Record<string, string> = {
    device_id: deviceId,
  };
  const start = toQueryIso(timeRange?.start);
  const end = toQueryIso(timeRange?.end);
  if (start !== undefined) {
    params.start = start;
  }
  if (end !== undefined) {
    params.end = end;
  }
  if (workspaceScope === "all") {
    params.workspace_scope = "all";
  }
  if (dashboardId) {
    params.dashboard_id = dashboardId;
  }
  if (moduleKey) {
    params.module_key = moduleKey;
  }
  const { data } = await apiClient.get<TelemetryDto[]>("/telemetry", {
    params,
    __skipTenantHeader: workspaceScope === "all",
  });
  return data;
}

export type TelemetryPageParams = {
  deviceId: string;
  timeRange?: TelemetryTimeRange;
  workspaceScope?: "all" | null;
  dashboardId?: string | null;
  moduleKey?: string | null;
  limit?: number;
  offset?: number;
  sortBy?: string;
  order?: TelemetrySortOrder;
  query?: string | null;
};

export async function getTelemetryPage(
  params: TelemetryPageParams,
): Promise<TelemetryPageResponseDto> {
  const query: Record<string, string | number> = {
    device_id: params.deviceId,
    limit: params.limit ?? 25,
    offset: params.offset ?? 0,
    sort_by: params.sortBy ?? "time",
    order: params.order ?? "desc",
  };
  const start = toQueryIso(params.timeRange?.start);
  const end = toQueryIso(params.timeRange?.end);
  if (start !== undefined) {
    query.start = start;
  }
  if (end !== undefined) {
    query.end = end;
  }
  if (params.workspaceScope === "all") {
    query.workspace_scope = "all";
  }
  if (params.dashboardId) {
    query.dashboard_id = params.dashboardId;
  }
  if (params.moduleKey) {
    query.module_key = params.moduleKey;
  }
  if (params.query?.trim()) {
    query.q = params.query.trim();
  }
  const { data } = await apiClient.get<TelemetryPageResponseDto>("/telemetry/page", {
    params: query,
    __skipTenantHeader: params.workspaceScope === "all",
  });
  return data;
}

export type ExportTelemetryParams = {
  deviceId: string;
  format: "csv" | "pdf";
  timeRange?: TelemetryTimeRange;
  workspaceScope?: "all" | null;
  dashboardId?: string | null;
  moduleKey?: string | null;
  sortBy?: string;
  order?: TelemetrySortOrder;
  query?: string | null;
  channels?: string[];
};

export async function exportTelemetry(
  params: ExportTelemetryParams,
): Promise<{ blob: Blob; fileName: string | null }> {
  const query: Record<string, string> = {
    device_id: params.deviceId,
    format: params.format,
    sort_by: params.sortBy ?? "time",
    order: params.order ?? "desc",
  };
  const start = toQueryIso(params.timeRange?.start);
  const end = toQueryIso(params.timeRange?.end);
  if (start !== undefined) {
    query.start = start;
  }
  if (end !== undefined) {
    query.end = end;
  }
  if (params.workspaceScope === "all") {
    query.workspace_scope = "all";
  }
  if (params.dashboardId) {
    query.dashboard_id = params.dashboardId;
  }
  if (params.moduleKey) {
    query.module_key = params.moduleKey;
  }
  if (params.query?.trim()) {
    query.q = params.query.trim();
  }
  if (params.channels?.length) {
    query.channels = params.channels.join(",");
  }
  const response = await apiClient.get<Blob>("/telemetry/export", {
    params: query,
    responseType: "blob",
    __skipTenantHeader: params.workspaceScope === "all",
  });
  const disposition = response.headers["content-disposition"] as string | undefined;
  const fileNameMatch = disposition?.match(/filename="([^"]+)"/i);
  return {
    blob: response.data,
    fileName: fileNameMatch?.[1] ?? null,
  };
}

export async function getLiveTelemetry(
  deviceId: string,
  timeRange?: TelemetryTimeRange,
  workspaceScope?: "all" | null,
  dashboardId?: string | null,
): Promise<TelemetryDto[]> {
  const params: Record<string, string> = {
    device_id: deviceId,
  };
  const start = toQueryIso(timeRange?.start);
  const end = toQueryIso(timeRange?.end);
  if (start !== undefined) {
    params.start = start;
  }
  if (end !== undefined) {
    params.end = end;
  }
  if (workspaceScope === "all") {
    params.workspace_scope = "all";
  }
  if (dashboardId) {
    params.dashboard_id = dashboardId;
  }
  const { data } = await apiClient.get<TelemetryDto[]>("/telemetry/live", {
    params,
    __skipTenantHeader: workspaceScope === "all",
  });
  return data;
}
